import React from 'react';
import { CircularProgress, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import {
  computeProductPrice,
  Modifier
} from '@pizza-hut-us-development/client-core';
import colors from '../../../common/colors';
import formattedPrice from '../../../common/formattedPrice';
import { selectors } from '../slice/pizza.slice';
import {
  cartTotalPriceSelectors,
  updateCurrentBuilderPrice,
  updatePriceIsLoading
} from './CartTotalPrice.slice';
import { useDeepEffectEfficiently } from '../../../common/useDeepEffectEfficiently';
import { useDebounceSelector } from '../../../common/useDebounceSelector';
import { RootState } from '@/rootStateTypes';
import {
  useCCTransformLegacyCartItem
} from '@/clientCore/temporaryTransformationalHooks/useCCTransformLegacyCartItem/useCCTransformLegacyCartItem';
import { AddableCartItem } from '@/api/phdApiV2Client/request.types';

const useStyles = makeStyles({
  price: {
    paddingTop: '3px',
    color: (priceIsLoading) => (priceIsLoading ? colors.gray400 : colors.gray900)
  },
  loadingIndicator: {
    position: 'absolute',
    color: colors.gray601,
    left: '38%',
    marginTop: '-2px',
    width: '24px',
    height: '24px'
  },
  carToPriceContainer: {
    position: 'relative',
    minHeight: '34px',
    minWidth: '110px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
});

const getLocalizationToken = (state: RootState): StoreDetail['storeToken'] => state?.domain?.localization?.localizedStoreDetails?.storeToken;

const CartTotalPrice = (): JSX.Element | null => {
  const pizza = useDebounceSelector(selectors.selectCartAddablePizza);
  const pizzaOptions = useSelector(selectors.selectPizzaOptions);
  const shouldShowPrice = useSelector(selectors.showTotalPrice);
  const totalPriceInCents = useSelector(
    cartTotalPriceSelectors.selectTotalInCents
  );
  const priceIsLoading = useSelector(cartTotalPriceSelectors.selectIsLoading);
  const classes = useStyles(priceIsLoading);
  const localizationToken = useSelector(getLocalizationToken);
  const dispatch = useDispatch();
  const [enableHidePriceLoader] = useDecision('ops-web2153-hide-pb-loader-yum');
  const pizzaToPrice = { ...pizza, quantity: 1, specialInstructions: '' };
  const { handleLegacyCartItemTransformer } = useCCTransformLegacyCartItem();

  const getYumPizzaPrice = (): number => {
    const { modifiers } = handleLegacyCartItemTransformer(pizzaToPrice as AddableCartItem) as Modifier;
    const selectedPizzaSize = pizzaOptions?.sizes?.find((option) => option.id === pizzaToPrice?.size?.id);
    const selectedPizzaCrust = pizzaOptions?.crusts?.find((option) => option.variantCode === pizzaToPrice?.crust?.variantCode);

    modifiers?.push({
      ...selectedPizzaSize,
      optionTypeCode: 'option_pizza_size',
      displayOrder: selectedPizzaSize?.priority
    } as unknown as Modifier,
    {
      ...selectedPizzaCrust,
      optionTypeCode: 'option_pizza_crust',
      displayOrder: selectedPizzaSize?.priority
    } as unknown as Modifier);

    return computeProductPrice(modifiers as Modifier[]);
  };

  const handleCCPHDPizzaBuilderPricing = async () => {
    dispatch(updateCurrentBuilderPrice({ totalPriceInCents: getYumPizzaPrice() || null }));
    dispatch(updatePriceIsLoading(false));
  };

  useDeepEffectEfficiently(async () => {
    if (!shouldShowPrice) return Promise.resolve();
    dispatch(updatePriceIsLoading(true));

    // if using CC phd display pricing using cc hook
    return handleCCPHDPizzaBuilderPricing();
  }, [pizzaToPrice, localizationToken, shouldShowPrice]);

  if (!shouldShowPrice) return null;

  return (
    <div className={classes.carToPriceContainer}>
      {enableHidePriceLoader.enabled
        ? null
        : priceIsLoading && (
        <CircularProgress className={classes.loadingIndicator} size={24} />
        )}
      <Typography
        variant="h2"
        className={classes.price}
        aria-label="Cart total price"
      >
        {totalPriceInCents !== null && formattedPrice(totalPriceInCents)}
      </Typography>
    </div>
  );
};

export default CartTotalPrice;
